import axios from 'axios';
import React, { useState, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive'
import { useNavigate } from 'react-router-dom';

function ResetPasswordPage({ language, onLanguageChange }) {
    const isXL = useMediaQuery({ query: '(min-width: 1824px)' });
    const isLG = useMediaQuery({ query: '(min-width: 1224px)' }) && !isXL;
    const isMD = useMediaQuery({ query: '(min-width: 768px)' }) && !isLG && !isXL;
    const isSM = useMediaQuery({ query: '(min-width: 425px)' }) && !isMD && !isLG && !isXL;
    const isXS = useMediaQuery({ query: '(max-width: 425px)' });
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState({
        hasUpperCase: true,
        hasLowerCase: true,
        hasNumber: true,
        isLongEnough: true,
        allValid: true
    });
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [isOobCodeValid, setIsOobCodeValid] = useState(false);
    const [showNotValidOobCodeMessage, setShowNotValidOobCodeMessage] = useState(false);

    const searchParams = new URLSearchParams(window.location.search);
    const oobCode = searchParams.get('oobCode');
    const mode = searchParams.get('mode');
    const languageParam = searchParams.get('lang');
    const userId = searchParams.get('userId');

    useEffect(() => {
        if (languageParam !== language && (languageParam === "en" || languageParam === "de")) {
            onLanguageChange(languageParam);
        }
    }, []);

    const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;

    useEffect(() => {
        if (!oobCode || !apiKey || mode !== "resetPassword") {
            navigate("/", { replace: true });
        } else {
            axios.patch(`${process.env.REACT_APP_API_URL}user/checkOobCodeValidation`, {
                oobCode: oobCode,
                mode: mode,
                apiKey: apiKey
            })
                .catch((err) => {
                    console.log("err: " + err);
                    // navigate("/", { replace: true });
                    setShowNotValidOobCodeMessage(true);
                })
                .then((response) => {
                    if (response && response.data.success === true && response.data.status === 200) {
                        // OobCode is valid, do nothing
                        setIsOobCodeValid(true);
                    } else {
                        // navigate("/", { replace: true });
                        setShowNotValidOobCodeMessage(true);
                    }
                });
        }
    }, [oobCode, mode, apiKey, navigate]);

    const validatePassword = (password) => {
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const isLongEnough = password.length >= 8;
        return {
            allValid: hasUpperCase && hasLowerCase && hasNumber && isLongEnough,
            hasUpperCase,
            hasLowerCase,
            hasNumber,
            isLongEnough
        };
    };

    useEffect(() => {
        setPasswordError(validatePassword(password));
        setConfirmPasswordError(false);
        setSuccessMessage("");

        if (!password && !confirmPassword) return;

        if (password !== confirmPassword) {
            setConfirmPasswordError(true);
        }

        if (validatePassword(password) && password === confirmPassword) {
            setSuccessMessage("");
        }
    }, [password, confirmPassword]);

    const handleFormSubmit = (e) => {
        e.preventDefault();

        if (!passwordError.allValid || confirmPasswordError || !password || !confirmPassword) {
            return;
        }

        axios.patch(`${process.env.REACT_APP_API_URL}user/resetPassword`, {
            oobCode: oobCode,
            newPassword: password,
            apiKey: apiKey,
            userId: userId
        })
            .then((response) => {
                if (response && response.data.success === true && response.data.status === 201) {
                    setSuccessMessage("password_reset_success");
                    setTimeout(() => {
                        navigate("/", { replace: true });
                    }, 1000);
                } else {
                    setSuccessMessage("password_reset_failed");
                }
            })
            .catch((err) => {
                console.log("err: " + err);
                setSuccessMessage("password_reset_failed");
            });
    }

    if (showNotValidOobCodeMessage) {
        return (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "65vh" }}>
                <h2 style={{ fontSize: "24px", fontWeight: 900, margin: 0 }}>
                    <FormattedMessage id="invalid_password_reset_link" />
                </h2>
            </div>
        )
    }

    if (!isOobCodeValid) {
        return <div
            style={{
                backgroundColor: "white",
                height: "100vh"
            }}
        />
    }

    return (
        <div style={{
            padding: (isSM || isXS) ? "30px 50px 75px 50px" : "30px 180px 75px 180px",
            backgroundColor: "white",
            color: "#212121",
            fontSize: "16px",
            minHeight: (isXL || isLG) ? "55vh" : "40vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
        }}>
            <h1 style={{
                textAlign: "center",
                fontWeight: "700",
                fontSize: "30px",
            }}>
                <FormattedMessage id='reset_password' />
            </h1>

            <div style={{ marginTop: "36px" }}>
                <form
                    onSubmit={handleFormSubmit}
                    role="presentation"
                    autoComplete="off"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "20px",
                        maxWidth: "300px",
                    }}>

                    <div style={{ width: "100%" }}>
                        <p style={{ fontWeight: "600", fontSize: "16px", margin: "0 0 6px 0" }}><FormattedMessage id='password' /> <span style={{ color: "red" }}>*</span></p>
                        <div style={{
                            position: "relative"
                        }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder={formatMessage({ id: 'type_your_password' })}
                                role="presentation"
                                autoComplete="new-password"
                                name="new-psw"
                                style={{
                                    width: "280px",
                                    padding: "10px",
                                    borderRadius: "14px",
                                    border: `2px solid ${password && !passwordError.allValid ? "#ED2626" : "#ccc"}`,
                                    fontSize: "16px",
                                }}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <span
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: "absolute",
                                    top: "23px",
                                    right: "10px",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    userSelect: "none",
                                }}
                            >
                                {showPassword ?
                                    <img src="/assets/images/obsecure-text-icon.png" alt="eye" style={{ width: "20px", height: "20px" }} />
                                    :
                                    <img src="/assets/images/unobsecure-text-icon.png" alt="eye" style={{ width: "20px", height: "20px" }} />
                                }
                            </span>
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "10px",
                        }}>
                            <img
                                src={`/assets/images/${!password || !passwordError.isLongEnough ? "close" : "check"}-circle.svg`}
                                alt="warning" style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "5px",
                                    filter: !password ? "" : passwordError.isLongEnough ? "invert(58%) sepia(31%) saturate(1087%) hue-rotate(75deg) brightness(91%) contrast(85%)" : "invert(23%) sepia(98%) saturate(7484%) hue-rotate(357deg) brightness(89%) contrast(124%)",
                                }} />
                            <p style={{
                                fontSize: "12px",
                                margin: "0",
                                color: !password ? "#999" : passwordError.isLongEnough ? "#289152" : "#ED2626",
                            }}>
                                <FormattedMessage id="must_be_minimum_8_characters" />
                            </p>
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "5px",
                        }}>
                            <img
                                src={`/assets/images/${!password || !passwordError.hasUpperCase ? "close" : "check"}-circle.svg`}
                                alt="warning" style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "5px",
                                    filter: !password ? "" : passwordError.hasUpperCase ? "invert(58%) sepia(31%) saturate(1087%) hue-rotate(75deg) brightness(91%) contrast(85%)" : "invert(23%) sepia(98%) saturate(7484%) hue-rotate(357deg) brightness(89%) contrast(124%)",
                                }} />
                            <p style={{
                                fontSize: "12px",
                                margin: "0",
                                color: !password ? "#999" : passwordError.hasUpperCase ? "#289152" : "#ED2626",
                            }}>
                                <FormattedMessage id="must_contain_at_least_one_uppercase_letter" />
                            </p>
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "5px",
                        }}>
                            <img
                                src={`/assets/images/${!password || !passwordError.hasLowerCase ? "close" : "check"}-circle.svg`}
                                alt="warning" style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "5px",
                                    filter: !password ? "" : passwordError.hasLowerCase ? "invert(58%) sepia(31%) saturate(1087%) hue-rotate(75deg) brightness(91%) contrast(85%)" : "invert(23%) sepia(98%) saturate(7484%) hue-rotate(357deg) brightness(89%) contrast(124%)",
                                }} />
                            <p style={{
                                fontSize: "12px",
                                margin: "0",
                                color: !password ? "#999" : passwordError.hasLowerCase ? "#289152" : "#ED2626",
                            }}>
                                <FormattedMessage id="must_contain_at_least_one_lowercase_letter" />
                            </p>
                        </div>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: "5px",
                        }}>
                            <img
                                src={`/assets/images/${!password || !passwordError.hasNumber ? "close" : "check"}-circle.svg`}
                                alt="warning" style={{
                                    width: "16px",
                                    height: "16px",
                                    marginRight: "5px",
                                    filter: !password ? "" : passwordError.hasNumber ? "invert(58%) sepia(31%) saturate(1087%) hue-rotate(75deg) brightness(91%) contrast(85%)" : "invert(23%) sepia(98%) saturate(7484%) hue-rotate(357deg) brightness(89%) contrast(124%)",
                                }} />
                            <p style={{
                                fontSize: "12px",
                                margin: "0",
                                color: !password ? "#999" : passwordError.hasNumber ? "#289152" : "#ED2626",
                            }}>
                                <FormattedMessage id="must_contain_at_least_one_digit" />
                            </p>
                        </div>
                    </div>

                    <div style={{ width: "100%" }}>
                        <p style={{ fontWeight: "600", fontSize: "16px", margin: "0 0 6px 0" }}><FormattedMessage id='password_confirmation' /> <span style={{ color: "red" }}>*</span></p>
                        <div style={{ position: "relative" }}>
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder={formatMessage({ id: 'type_password_again' })}
                                role="presentation"
                                autoComplete="new-password"
                                name="confirm-psw"
                                style={{
                                    width: "280px",
                                    padding: "10px",
                                    borderRadius: "14px",
                                    border: `2px solid ${confirmPasswordError ? "#ED2626" : "#ccc"}`,
                                    fontSize: "16px",
                                }}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                            <span
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                style={{
                                    position: "absolute",
                                    top: "23px",
                                    right: "10px",
                                    transform: "translateY(-50%)",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    userSelect: "none",
                                }}
                            >
                                {showConfirmPassword ?
                                    <img src="/assets/images/obsecure-text-icon.png" alt="eye" style={{ width: "20px", height: "20px" }} />
                                    :
                                    <img src="/assets/images/unobsecure-text-icon.png" alt="eye" style={{ width: "20px", height: "20px" }} />}
                            </span>
                        </div>
                        {confirmPasswordError && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                marginTop: "5px",
                            }}>
                                <img
                                    src={`/assets/images/close-circle.svg`}
                                    alt="warning" style={{
                                        width: "16px",
                                        height: "16px",
                                        marginRight: "5px",
                                        filter: "invert(23%) sepia(98%) saturate(7484%) hue-rotate(357deg) brightness(89%) contrast(124%)",
                                    }} />
                                <p style={{
                                    fontSize: "12px",
                                    margin: "0",
                                    color: "#ED2626",
                                }}>
                                    <FormattedMessage id='passwords_do_not_match' />
                                </p>
                            </div>
                        )}
                    </div>

                    {successMessage && (
                        <div style={{
                            color: successMessage && successMessage === "password_reset_failed" ? "#ED2626" : "#289152",
                            fontSize: "14px",
                            fontWeight: "bold",
                            textAlign: "center",
                            marginBottom: "10px",
                        }}>
                            <FormattedMessage id={successMessage} />
                        </div>
                    )}

                    <button
                        type="submit"
                        style={{
                            width: "100%",
                            padding: "10px",
                            borderRadius: "14px",
                            border: "none",
                            backgroundColor: (!password || !confirmPassword || !passwordError.allValid || confirmPasswordError) ? "rgba(28,28,28,0.1)" : "#8C52FF",
                            color: (!password || !confirmPassword || !passwordError.allValid || confirmPasswordError) ? "rgba(28,28,28,0.15)" : "white",
                            fontSize: "16px",
                            cursor: (!password || !confirmPassword || !passwordError.allValid || confirmPasswordError) ? "" : "pointer",
                        }}
                        disabled={(!password || !confirmPassword || !passwordError.allValid || confirmPasswordError)}
                    >
                        <FormattedMessage id='reset_password' />
                    </button>
                </form>
            </div>
        </div>
    )
}

export default ResetPasswordPage;
